import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import { adminApi, catalogApi, type AdminLocation, type AdminVendor } from '@/api/endpoints';
import { queryKeys } from '@/api/queryKeys';
import type { AssetModel, AssetType } from '@/api/types';
import { Button } from '@/design-system/Button';
import { InlineError } from '@/design-system/Feedback';
import { Select, TextInput } from '@/design-system/Inputs';
import { Dialog } from '@/design-system/Overlay';
import { humanise } from '@/lib/format';

/**
 * Adding a type or a model without leaving the asset you are registering.
 *
 * A new company starts with an empty catalogue, so the first person to register anything finds
 * two required pickers with nothing in them and no way forward from that screen. Sending them to
 * Settings to build a catalogue first is correct in principle and wrong in practice: they are
 * holding the device now.
 *
 * These dialogs therefore ask for the few fields that cannot be guessed and leave the rest at
 * sensible defaults, which Settings → Asset catalogue can refine later. They are not a second
 * implementation of that screen - the same admin endpoints, the same validation, the same
 * permission.
 */

const CATEGORIES = [
  'LAPTOP',
  'DESKTOP',
  'MONITOR',
  'MOBILE',
  'TABLET',
  'NETWORK',
  'PERIPHERAL',
  'SERVER',
  'OTHER',
];

/** Every catalogue list the new row belongs to, so the picker behind this dialog refreshes. */
const CATALOG_KEYS = [queryKeys.admin.all, ['catalog']] as const;

interface TypeForm {
  name: string;
  code: string;
  category: string;
}

export function QuickAssetTypeDialog({
  open,
  category,
  onClose,
  onCreated,
}: {
  open: boolean;
  /** Fixed when the caller is adding an accessory or a general item, free otherwise. */
  category?: string;
  onClose: () => void;
  onCreated: (type: AssetType) => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const { register, handleSubmit, reset, setError: setFieldError, formState } = useForm<TypeForm>({
    defaultValues: { name: '', code: '', category: category ?? 'LAPTOP' },
  });

  const close = () => {
    if (saving) return;
    reset();
    setError(null);
    onClose();
  };

  const submit = handleSubmit(async (form) => {
    setSaving(true);
    setError(null);
    try {
      const created = await adminApi.createAssetType({
        code: form.code.trim().toUpperCase(),
        fields: {
          name: form.name.trim(),
          category: form.category,
          // Defaults chosen to be the least surprising: a registered device should carry a tag,
          // and the three obligations that create work for somebody are off until asked for.
          requiresAssetTag: true,
          requiresEndpointAgent: false,
          requiresMdmEnrolment: false,
          requiresSanitisation: false,
        },
      });
      CATALOG_KEYS.forEach((key) => void queryClient.invalidateQueries({ queryKey: key }));
      toast.success('Asset type added', { description: created.name ?? form.name });
      reset();
      onCreated(created);
    } catch (caught) {
      if (caught instanceof ApiError && caught.fieldErrors.length > 0) {
        caught.fieldErrors.forEach((fieldError) => {
          const field = fieldError.field.replace(/^fields\./, '') as keyof TypeForm;
          setFieldError(field, { message: fieldError.message });
        });
      } else {
        setError(caught);
      }
    } finally {
      setSaving(false);
    }
  });

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Add an asset type"
      description="A kind of thing you own - laptops, monitors, phones. Tag and agent requirements can be set later under Settings → Asset catalogue."
      busy={saving}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void submit()}>
            Add type
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(event) => void submit(event)} noValidate>
        {error ? <InlineError error={error} /> : null}

        <TextInput
          label="Name"
          required
          autoFocus
          placeholder="Laptop"
          error={formState.errors.name?.message}
          {...register('name', { required: 'A name is required' })}
        />
        <TextInput
          label="Code"
          required
          placeholder="LAPTOP"
          description="A short identifier, used in exports and imports. Letters, digits and underscores."
          error={formState.errors.code?.message}
          {...register('code', {
            required: 'A code is required',
            pattern: {
              value: /^[A-Za-z0-9_]+$/,
              message: 'Use letters, digits and underscores only',
            },
          })}
        />
        <Select
          label="Category"
          required
          // Locked when the caller already decided: adding an accessory must not produce a laptop.
          disabled={Boolean(category)}
          description={
            category
              ? 'Fixed by the list you are adding to.'
              : 'Groups the type on dashboards and decides which screens it appears on.'
          }
          options={CATEGORIES.map((value) => ({ value, label: humanise(value) }))}
          {...register('category', { required: true })}
        />
      </form>
    </Dialog>
  );
}

interface ModelForm {
  manufacturerId: string;
  newManufacturerName: string;
  modelName: string;
  modelNumber: string;
}

const NEW_MANUFACTURER = '__new__';

export function QuickAssetModelDialog({
  open,
  assetTypeId,
  onClose,
  onCreated,
}: {
  open: boolean;
  assetTypeId: string;
  onClose: () => void;
  onCreated: (model: AssetModel) => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const { register, handleSubmit, reset, watch, setError: setFieldError, formState } =
    useForm<ModelForm>({
      defaultValues: { manufacturerId: '', newManufacturerName: '', modelName: '', modelNumber: '' },
    });

  const manufacturers = useQuery({
    queryKey: queryKeys.catalog.manufacturers(),
    queryFn: catalogApi.manufacturers,
    enabled: open,
    staleTime: 10 * 60_000,
  });

  const manufacturerId = watch('manufacturerId');
  // A model needs a manufacturer, and a new company has none - so one can be created here too,
  // rather than sending somebody to a third screen to unblock the second.
  const creatingManufacturer =
    manufacturerId === NEW_MANUFACTURER || (manufacturers.isSuccess && manufacturers.data.length === 0);

  const close = () => {
    if (saving) return;
    reset();
    setError(null);
    onClose();
  };

  const submit = handleSubmit(async (form) => {
    setSaving(true);
    setError(null);
    try {
      let chosenManufacturerId = form.manufacturerId;
      if (creatingManufacturer) {
        const manufacturer = await adminApi.createManufacturer({
          name: form.newManufacturerName.trim(),
          active: true,
        });
        chosenManufacturerId = manufacturer.id ?? '';
      }

      const created = await adminApi.createAssetModel({
        manufacturerId: chosenManufacturerId,
        assetTypeId,
        modelName: form.modelName.trim(),
        modelNumber: form.modelNumber.trim() || undefined,
      });
      CATALOG_KEYS.forEach((key) => void queryClient.invalidateQueries({ queryKey: key }));
      toast.success('Model added', { description: created.modelName ?? form.modelName });
      reset();
      onCreated(created);
    } catch (caught) {
      if (caught instanceof ApiError && caught.fieldErrors.length > 0) {
        caught.fieldErrors.forEach((fieldError) => {
          const field = fieldError.field as keyof ModelForm;
          setFieldError(field, { message: fieldError.message });
        });
      } else {
        setError(caught);
      }
    } finally {
      setSaving(false);
    }
  });

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Add a model"
      description="The specific product - a MacBook Pro 14, a ThinkPad T14. It belongs to the asset type you chose."
      busy={saving}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void submit()}>
            Add model
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(event) => void submit(event)} noValidate>
        {error ? <InlineError error={error} /> : null}

        {manufacturers.isSuccess && manufacturers.data.length > 0 ? (
          <Select
            label="Manufacturer"
            required
            placeholder="Choose a manufacturer"
            error={formState.errors.manufacturerId?.message}
            options={[
              ...manufacturers.data.map((manufacturer) => ({
                value: manufacturer.id ?? '',
                label: manufacturer.name ?? '',
              })),
              { value: NEW_MANUFACTURER, label: 'Add a new manufacturer…' },
            ]}
            {...register('manufacturerId', { required: 'Choose a manufacturer' })}
          />
        ) : null}

        {creatingManufacturer ? (
          <TextInput
            label="New manufacturer"
            required
            autoFocus
            placeholder="Dell"
            description={
              manufacturers.isSuccess && manufacturers.data.length === 0
                ? 'No manufacturers yet, so this one is created with the model.'
                : undefined
            }
            error={formState.errors.newManufacturerName?.message}
            {...register('newManufacturerName', {
              validate: (value) =>
                !creatingManufacturer || value.trim().length > 0 || 'A manufacturer name is required',
            })}
          />
        ) : null}

        <TextInput
          label="Model name"
          required
          placeholder="ThinkPad T14 Gen 4"
          error={formState.errors.modelName?.message}
          {...register('modelName', { required: 'A model name is required' })}
        />
        <TextInput
          label="Model number"
          placeholder="21HD0000US"
          description="Optional. The manufacturer's part number, where it is known."
          error={formState.errors.modelNumber?.message}
          {...register('modelNumber')}
        />
      </form>
    </Dialog>
  );
}

interface LocationForm {
  name: string;
  code: string;
  city: string;
}

export function QuickLocationDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (location: AdminLocation) => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const { register, handleSubmit, reset, setError: setFieldError, formState } = useForm<LocationForm>({
    defaultValues: { name: '', code: '', city: '' },
  });

  const close = () => {
    if (saving) return;
    reset();
    setError(null);
    onClose();
  };

  const submit = handleSubmit(async (form) => {
    setSaving(true);
    setError(null);
    try {
      const created = await adminApi.createLocation({
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        address: form.city.trim() ? { city: form.city.trim() } : undefined,
      });
      queryClient.setQueryData<AdminLocation[]>(queryKeys.organization.locations(), (current = []) =>
        current.some((loc) => loc.id === created.id) ? current : [...current, created],
      );
      [queryKeys.organization.locations(), queryKeys.admin.locations(), queryKeys.admin.all].forEach(
        (key) => void queryClient.invalidateQueries({ queryKey: key }),
      );
      toast.success('Location added', { description: created.name ?? form.name });
      reset();
      onCreated(created);
    } catch (caught) {
      if (caught instanceof ApiError && caught.fieldErrors.length > 0) {
        caught.fieldErrors.forEach((fieldError) => {
          const field = fieldError.field.replace(/^address\./, '') as keyof LocationForm;
          setFieldError(field, { message: fieldError.message });
        });
      } else {
        setError(caught);
      }
    } finally {
      setSaving(false);
    }
  });

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Add a location"
      description="A site, office or facility where assets are kept. Full address details can be added later under Settings → Organisation."
      busy={saving}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void submit()}>
            Add location
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(event) => void submit(event)} noValidate>
        {error ? <InlineError error={error} /> : null}

        <TextInput
          label="Name"
          required
          autoFocus
          placeholder="e.g. London Office"
          error={formState.errors.name?.message}
          {...register('name', { required: 'A name is required' })}
        />
        <TextInput
          label="Code"
          required
          placeholder="LON-OFFICE"
          description="A short identifier. Letters, digits, underscores and hyphens."
          error={formState.errors.code?.message}
          {...register('code', {
            required: 'A code is required',
            pattern: {
              value: /^[A-Za-z0-9_-]+$/,
              message: 'Use letters, digits, underscores and hyphens only',
            },
          })}
        />
        <TextInput
          label="City"
          placeholder="London"
          error={formState.errors.city?.message}
          {...register('city')}
        />
      </form>
    </Dialog>
  );
}

interface VendorForm {
  name: string;
  code: string;
  contactEmail: string;
  contactPhone: string;
}

export function QuickVendorDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (vendor: AdminVendor) => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const { register, handleSubmit, reset, setError: setFieldError, formState } = useForm<VendorForm>({
    defaultValues: { name: '', code: '', contactEmail: '', contactPhone: '' },
  });

  const close = () => {
    if (saving) return;
    reset();
    setError(null);
    onClose();
  };

  const submit = handleSubmit(async (form) => {
    setSaving(true);
    setError(null);
    try {
      const created = await adminApi.createVendor({
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        contactEmail: form.contactEmail.trim() || undefined,
        contactPhone: form.contactPhone.trim() || undefined,
      });
      queryClient.setQueryData<AdminVendor[]>(queryKeys.catalog.vendors(), (current = []) =>
        current.some((v) => v.id === created.id) ? current : [...current, created],
      );
      [queryKeys.catalog.vendors(), queryKeys.admin.vendors(), ...CATALOG_KEYS].forEach(
        (key) => void queryClient.invalidateQueries({ queryKey: key }),
      );
      toast.success('Vendor added', { description: created.name ?? form.name });
      reset();
      onCreated(created);
    } catch (caught) {
      if (caught instanceof ApiError && caught.fieldErrors.length > 0) {
        caught.fieldErrors.forEach((fieldError) => {
          const field = fieldError.field as keyof VendorForm;
          setFieldError(field, { message: fieldError.message });
        });
      } else {
        setError(caught);
      }
    } finally {
      setSaving(false);
    }
  });

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Add a vendor"
      description="A supplier, dealer or service provider. Contact details can be refined later under Settings → Asset catalogue."
      busy={saving}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void submit()}>
            Add vendor
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(event) => void submit(event)} noValidate>
        {error ? <InlineError error={error} /> : null}

        <TextInput
          label="Name"
          required
          autoFocus
          placeholder="e.g. Dell"
          error={formState.errors.name?.message}
          {...register('name', { required: 'A name is required' })}
        />
        <TextInput
          label="Code"
          required
          placeholder="DELL"
          description="A short identifier. Letters, digits, underscores and hyphens."
          error={formState.errors.code?.message}
          {...register('code', {
            required: 'A code is required',
            pattern: {
              value: /^[A-Za-z0-9_-]+$/,
              message: 'Use letters, digits, underscores and hyphens only',
            },
          })}
        />
        <TextInput
          label="Contact email"
          type="email"
          placeholder="orders@vendor.com"
          error={formState.errors.contactEmail?.message}
          {...register('contactEmail', {
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$|^$/,
              message: 'Enter a valid email address',
            },
          })}
        />
        <TextInput
          label="Contact phone"
          type="tel"
          placeholder="+1 555 0199"
          error={formState.errors.contactPhone?.message}
          {...register('contactPhone')}
        />
      </form>
    </Dialog>
  );
}
